import { Controller } from "@hotwired/stimulus"

// Portfolio dashboard Branding modal.
//
// - Color pickers + matching hex text inputs stay in sync.
// - Live preview pane reflects colors and footer text without a round trip.
// - Save PATCHes companies#update_portfolio_theme and swaps the dashboard
//   turbo frame so the new theme applies without a full page reload.
//
// The logo lives on Company#logo and is managed from the Company Settings
// modal — not here — so the two uploaders never disagree.

const HEX = /^#(?:[0-9a-fA-F]{3}|[0-9a-fA-F]{6})$/

export default class extends Controller {
  static targets = [
    "primaryPicker", "primaryText",
    "accentPicker", "accentText",
    "footerInput",
    "previewHeader",
    "previewAccentBar", "previewAccentDot",
    "previewButton", "previewFooter",
    "submitBtn", "errors"
  ]
  static values = {
    updateUrl: String,
    defaults: Object,
    initial: Object
  }

  connect() {
    this._renderPreview()
  }

  onColorInput(event) {
    const picker = event.currentTarget
    if (picker === this.primaryPickerTarget) {
      this.primaryTextTarget.value = picker.value.toUpperCase()
    } else if (picker === this.accentPickerTarget) {
      this.accentTextTarget.value = picker.value.toUpperCase()
    }
    this._renderPreview()
  }

  onTextInput(event) {
    const input = event.currentTarget
    const val = input.value.trim()
    if (HEX.test(val)) {
      if (input.dataset.role === "primary") this.primaryPickerTarget.value = this._normalizeHex(val)
      if (input.dataset.role === "accent")  this.accentPickerTarget.value  = this._normalizeHex(val)
    }
    this._renderPreview()
  }

  onFooterInput() {
    this._renderPreview()
  }

  resetDefaults() {
    const d = this.defaultsValue || {}
    this.primaryPickerTarget.value = d.primary_color || "#60a5ee"
    this.primaryTextTarget.value = (d.primary_color || "#60a5ee").toUpperCase()
    this.accentPickerTarget.value = d.accent_color || "#fb923c"
    this.accentTextTarget.value = (d.accent_color || "#fb923c").toUpperCase()
    this.footerInputTarget.value = d.footer_text || ""
    this._renderPreview()
  }

  async save() {
    this.errorsTarget.classList.add("hidden")
    this._setSubmitting(true)

    const body = new FormData()
    body.append("primary_color", this.primaryTextTarget.value.trim())
    body.append("accent_color", this.accentTextTarget.value.trim())
    body.append("footer_text", this.footerInputTarget.value.trim())

    try {
      const res = await fetch(this.updateUrlValue, {
        method: "PATCH",
        headers: { "X-CSRF-Token": this._csrf(), Accept: "application/json" },
        body
      })
      const data = await res.json()
      if (!res.ok || !data.success) {
        this._showError((data.errors || ["Save failed"]).join(", "))
        return
      }
      this._showToast("Branding updated", "notice")
      this.dispatch("close")
      this._reloadDashboard()
    } catch (e) {
      this._showError(e.message || "Network error")
    } finally {
      this._setSubmitting(false)
    }
  }

  _renderPreview() {
    const primary = this._safeHex(this.primaryTextTarget.value, "#60a5ee")
    const accent  = this._safeHex(this.accentTextTarget.value, "#fb923c")

    this.previewHeaderTarget.style.backgroundColor = primary
    this.previewButtonTarget.style.backgroundColor = primary
    this.previewAccentBarTarget.style.backgroundColor = accent
    this.previewAccentDotTarget.style.backgroundColor = accent

    const footer = this.footerInputTarget.value.trim()
    this.previewFooterTarget.textContent = footer || " "
  }

  _safeHex(val, fallback) {
    const s = (val || "").trim()
    return HEX.test(s) ? this._normalizeHex(s) : fallback
  }

  _normalizeHex(v) {
    if (v.length === 4) {
      return "#" + v.slice(1).split("").map(c => c + c).join("")
    }
    return v.toLowerCase()
  }

  _csrf() {
    return document.querySelector("meta[name='csrf-token']")?.content
  }

  _setSubmitting(flag) {
    this.submitBtnTarget.disabled = flag
    this.submitBtnTarget.textContent = flag ? "Saving…" : "Save"
  }

  _showError(msg) {
    this.errorsTarget.textContent = msg
    this.errorsTarget.classList.remove("hidden")
  }

  _showToast(msg, type = "notice") {
    const toast = document.createElement("div")
    toast.className = type === "alert" ? "flash-toast-alert" : "flash-toast-notice"
    toast.setAttribute("data-controller", "flash")
    toast.setAttribute("data-flash-dismiss-after-value", "5000")
    toast.textContent = msg
    document.body.prepend(toast)
  }

  _reloadDashboard() {
    const frame = document.getElementById("portfolio-dashboard-content")
    if (!frame) return
    frame.src = window.location.href
  }
}
